import { Dispatch, useEffect, useState } from 'react';
import Script from 'next/script';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { AnimatePresence, m } from 'framer-motion';
import cn from 'classnames';
import { useFeatureIsOn } from '@growthbook/growthbook-react';
import { Button, Close, Heading, Loading, Paragraph, StyledWordEmphasizer } from '@components';
import { sendPupillaryDistance } from '@services/poms/operations/send-pupillary-distance';
import { fade, slideUp } from '@utils/motions';
import { GLASSESON, useTranslation } from '@utils/index';
import { useLoadGlassesOn } from '@services/glasseson';
import styles from './PdToolModal.module.scss';

type PdToolModalProps = {
	open: boolean;
	setOpen: Dispatch<boolean>;
	orderNumber?: string;
	setPdToolInit: (open: boolean) => void;
	setPdToolHasError: (open: boolean) => void;
	pdValueHandler?: (pd: number) => void;
	strategy?: 'onLoad' | 'onReady';
	externalTrigger?: boolean;
	hasScriptLoaded?: boolean;
	onScriptLoaded?: () => void;
	setPdNeeded?: (pdNeeded: boolean) => void;
	className?: string;
	classNameOverlay?: string;
	isException?: boolean;
};

const getTranslatedTexts = translator => {
	return {
		pdReceived: translator('pd-received'),
		pdReceivedSuccessfully: (pd: string) => translator('pd-received-successfully', { pd }),
		errorNotification: translator('error-notification'),
		pdDatabaseIssue: (pd: string) => translator('pd-database-issue', { pd }),
		measuringToolIssue: translator('measuring-tool-issue'),
		errorUploadingPdMessage: translator('error-uploading-pd-meesage'),
		measurePdNow: translator('measure-pd-now'),
		close: translator('close-uppercase'),
	};
};

const PdToolModal = ({
	open,
	setOpen,
	orderNumber,
	setPdToolInit,
	setPdToolHasError,
	pdValueHandler,
	strategy = 'onLoad',
	externalTrigger = true,
	hasScriptLoaded = false,
	onScriptLoaded,
	setPdNeeded,
	className,
	classNameOverlay,
	isException,
	...props
}: PdToolModalProps) => {
	const [isLoading, setLoading] = useState(false);
	const [pd, setPd] = useState(null);
	const [isPdMeasuringToolActive, setPdMeasuringToolActive] = useState(false);
	const [isPomsSubmitSuccessful, setPomsSubmitSuccessful] = useState(false);
	const [hasAppError, setAppError] = useState(false);
	const [pdScriptLoaded, setPdScriptLoaded] = useState(hasScriptLoaded);
	const isVeeroPd = useFeatureIsOn('is-veero-pd');
	const { translator } = useTranslation();
	const translations = getTranslatedTexts(translator);

	let message = null;

	const handlePdMeasuringToolResult = result => {
		window.glasseson.close();
		setPdMeasuringToolActive(false);
		// Block default 'send-to-poms' behavior and do something else with the pd value
		setPd(result?.data?.pd);
		if (pdValueHandler) {
			pdValueHandler(result?.data?.pd);
		}
	};

	const pdToolInitialized = useLoadGlassesOn({ setAppError, handlePdMeasuringToolResult, pdScriptLoaded });

	useEffect(() => {
		setPdToolInit(pdToolInitialized);
		setPdToolHasError(hasAppError);

		// https://veero.gitbook.io/veero-eyesize-documentation/XTCTOPPO4ayo1DXkVt0J/eyesize/browser-sdk/site-integration
		const handleVeeroResults = results => {
			setPdMeasuringToolActive(false);
			if (results?.mono_pd) {
				setPd(results?.mono_pd);
				pdValueHandler && pdValueHandler(results?.mono_pd);
			}
		};

		if (pdToolInitialized && open && !pd) {
			!isVeeroPd &&
				!hasAppError &&
				window.glasseson
					.open('pd')
					.then(() => setPdMeasuringToolActive(true))
					.catch(e => {
						console.error('error opening pd tool', e);
						setAppError(true);
					});
			if (isVeeroPd) {
				window?.EyeSize?.launch(handleVeeroResults, handleVeeroResults, process.env.NEXT_PUBLIC_VEERO_ID, 4, 0, false);
			}
		}
	}, [pdToolInitialized, hasAppError, open, pd, isVeeroPd]);

	useEffect(() => {
		const submitPd = async () => {
			if (isException) {
				setPomsSubmitSuccessful(true);
				setAppError(false);
				setPdNeeded(false);
			} else {
				try {
					setLoading(true);
					await sendPupillaryDistance(orderNumber, pd);
					setLoading(false);
					setPomsSubmitSuccessful(true);
					setAppError(false);
					setPdNeeded(false);
				} catch (e) {
					setLoading(false);
					setAppError(true);
				}
			}
		};
		if (orderNumber && pd) {
			submitPd();
		}
	}, [pd, orderNumber, setPdNeeded, isException]);

	const resetPdMeasuringTool = () => {
		!isVeeroPd && window.glasseson.close();
		setPd(null);
		setPdMeasuringToolActive(false);
		setPomsSubmitSuccessful(false);
	};

	if (isPomsSubmitSuccessful) {
		message = (
			<>
				<Heading tag={'h2'}>{translations.pdReceived}</Heading>
				<Paragraph>
					<StyledWordEmphasizer
						text={translations.pdReceivedSuccessfully(pd)}
						emphasizedWords={String(pd)}
						styledTexts={<b>{pd}</b>}
					/>
				</Paragraph>
			</>
		);
	}

	if (hasAppError) {
		message = (
			<>
				<Heading tag={'h2'}>{translations.errorNotification}</Heading>
				<Paragraph>
					{pd ? (
						<StyledWordEmphasizer
							text={translations.pdDatabaseIssue(pd)}
							emphasizedWords={String(pd)}
							styledTexts={<b>{pd}</b>}
						/>
					) : (
						translations.measuringToolIssue
					)}
					<StyledWordEmphasizer
						text={translations.errorUploadingPdMessage}
						emphasizedWords={'hello@paireyewear.com'}
						styledTexts={<a href='mailto:hello@paireyewear.com'>hello@paireyewear.com</a>}
					/>
				</Paragraph>
			</>
		);
	}

	const handleOnLoad = () => {
		setPdScriptLoaded(true);
		onScriptLoaded && onScriptLoaded();
	};

	const glassOnScript =
		strategy === 'onLoad' ? (
			<Script src={GLASSESON.SCRIPT_URL} onLoad={() => handleOnLoad()} />
		) : (
			<Script src={GLASSESON.SCRIPT_URL} onReady={() => handleOnLoad()} />
		);

	const veeroScript = (
		<Script
			defer
			id='veero-location'
			src={`https://app.veero.ai/js/veero.main.js?b=${process.env.NEXT_PUBLIC_VEERO_ID}`}
			onLoad={() => handleOnLoad()}
		/>
	);

	return (
		<>
			{!isVeeroPd && glassOnScript}
			{isVeeroPd && (
				<>
					{veeroScript}
					<div id='veero-eyefit' />
				</>
			)}
			{!isVeeroPd && (
				<DialogPrimitive.Root open={!isVeeroPd ? open : false} onOpenChange={!isVeeroPd ? setOpen : null}>
					{!externalTrigger && (
						<DialogPrimitive.Trigger asChild>
							<Button label={translations.measurePdNow} color='white' fullWidth />
						</DialogPrimitive.Trigger>
					)}
					<AnimatePresence>
						<DialogPrimitive.Portal>
							<DialogPrimitive.Overlay asChild className={cn(styles['overlay'], classNameOverlay)}>
								<m.div {...fade}></m.div>
							</DialogPrimitive.Overlay>
							<DialogPrimitive.Content
								{...props}
								asChild
								className={cn(styles.content, className)}
								onPointerDownOutside={() => resetPdMeasuringTool()}
							>
								<m.div {...slideUp}>
									<div id='glasseson' />
									{!isPdMeasuringToolActive && (
										<div className={styles.message}>
											{isLoading ? <Loading className={styles.loading} /> : message}
										</div>
									)}
									<DialogPrimitive.Close aria-label={translations.close} className={styles['close']} asChild>
										<Button linkStyle removeEffects onClick={() => resetPdMeasuringTool()}>
											<Close label={translations.close} />
										</Button>
									</DialogPrimitive.Close>
								</m.div>
							</DialogPrimitive.Content>
						</DialogPrimitive.Portal>
					</AnimatePresence>
				</DialogPrimitive.Root>
			)}
		</>
	);
};

PdToolModal.displayName = 'PdToolModal';
export default PdToolModal;
